import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { drawerContentMaxHeight } from 'lib/universalSearchDrawerLayout';

/**
 * Results drawer for Slot Check Search. Same look as the Explore drawer
 * (.list-bottom-parent / .floating-panel / .handle / .drag-bar styles) with its
 * own small snap logic, because the Explore ListSheet is tied to the casino in
 * view (its middle stop moves while an EGM Details is open in that casino).
 *
 * Stops: collapsed (handle only), start (config %, default 50) and open (just
 * under the header). Drag or tap the handle to move between them.
 */
type Props = {
  children: ReactNode;
  header?: ReactNode;
  /** Open part of the screen at the start stop, 25–80 (config). */
  startPercent: number;
  /** Restore this open percent (globe icon return). */
  initialPercent?: number;
  /** Top of the drawer at the open stop (below the app header). */
  topOffset: number;
  onPercentChange?: (percent: number) => void;
  onScroll?: (scrollTop: number) => void;
  contentRef?: (el: HTMLDivElement | null) => void;
  /** Bump to move the drawer back to the start stop (e.g. a new search). */
  snapToStartSeq?: number;
};

const COLLAPSED_PX = 90;

const viewportHeight = () => (typeof window !== 'undefined' ? window.innerHeight : 800);

export const UniversalSearchDrawer = ({
  children,
  header,
  startPercent,
  initialPercent,
  topOffset,
  onPercentChange,
  onScroll,
  contentRef,
  snapToStartSeq,
}: Props) => {
  const [vh, setVh] = useState(viewportHeight());
  const yFromPercent = useCallback((pct: number) => Math.round(vh * (1 - pct / 100)), [vh]);
  const stops = useCallback(() => {
    const open = Math.max(0, topOffset);
    const start = Math.max(open, yFromPercent(startPercent));
    const collapsed = Math.max(start, vh - COLLAPSED_PX);
    return { open, start, collapsed };
  }, [topOffset, startPercent, vh, yFromPercent]);

  const [translateY, setTranslateY] = useState(() => {
    const pct = typeof initialPercent === 'number' ? initialPercent : startPercent;
    return Math.round(viewportHeight() * (1 - pct / 100));
  });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef<{ startY: number; startTranslate: number; moved: boolean } | null>(null);

  useEffect(() => {
    const onResize = () => setVh(viewportHeight());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // Keep inside the stops after a resize / header height change.
  useEffect(() => {
    const s = stops();
    setTranslateY((y) => Math.min(s.collapsed, Math.max(s.open, y)));
  }, [stops]);

  const report = useCallback(
    (y: number) => {
      const pct = Math.round((1 - y / vh) * 100);
      onPercentChange?.(Math.max(0, Math.min(100, pct)));
    },
    [onPercentChange, vh]
  );

  const snapTo = useCallback(
    (y: number) => {
      setTranslateY(y);
      report(y);
    },
    [report]
  );

  const nearestStop = (y: number, velocity: number) => {
    const s = stops();
    const list = [s.open, s.start, s.collapsed];
    if (Math.abs(velocity) > 0.6) {
      // Flick: next stop in the flick direction.
      const sorted = velocity < 0 ? list.filter((v) => v < y - 4).sort((a, b) => b - a) : list.filter((v) => v > y + 4).sort((a, b) => a - b);
      if (sorted.length) return sorted[0];
    }
    return list.reduce((best, v) => (Math.abs(v - y) < Math.abs(best - y) ? v : best), list[0]);
  };

  const lastSnapSeqRef = useRef(snapToStartSeq);
  useEffect(() => {
    if (snapToStartSeq === undefined || snapToStartSeq === lastSnapSeqRef.current) return;
    lastSnapSeqRef.current = snapToStartSeq;
    snapTo(stops().start);
  }, [snapToStartSeq, snapTo, stops]);

  const lastMoveRef = useRef<{ y: number; t: number } | null>(null);

  const onStart = (clientY: number) => {
    dragRef.current = { startY: clientY, startTranslate: translateY, moved: false };
    lastMoveRef.current = { y: clientY, t: Date.now() };
    setDragging(true);
  };
  const onMove = (clientY: number) => {
    const d = dragRef.current;
    if (!d) return;
    const s = stops();
    const delta = clientY - d.startY;
    if (Math.abs(delta) > 4) d.moved = true;
    setTranslateY(Math.min(s.collapsed, Math.max(s.open, d.startTranslate + delta)));
    lastMoveRef.current = { y: clientY, t: Date.now() };
  };
  const onEnd = (clientY: number) => {
    const d = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    if (!d) return;
    const s = stops();
    if (!d.moved) {
      // Tap: start → open → collapsed → start.
      const cur = translateY;
      const next = Math.abs(cur - s.start) < 4 ? s.open : Math.abs(cur - s.open) < 4 ? s.collapsed : s.start;
      snapTo(next);
      return;
    }
    const last = lastMoveRef.current;
    const dt = last ? Math.max(1, Date.now() - last.t) : 1;
    const velocity = last ? (clientY - last.y) / dt : 0;
    snapTo(nearestStop(translateY, velocity));
  };

  // Mouse drag (desktop preview) on the window so leaving the handle keeps dragging.
  useEffect(() => {
    if (!dragging) return;
    const mm = (e: MouseEvent) => onMove(e.clientY);
    const mu = (e: MouseEvent) => onEnd(e.clientY);
    window.addEventListener('mousemove', mm);
    window.addEventListener('mouseup', mu);
    return () => {
      window.removeEventListener('mousemove', mm);
      window.removeEventListener('mouseup', mu);
    };
  });

  // Height of everything above the list (handle + header: title, toggle, notices),
  // measured, so the list ends at the bottom of the screen and its last card is
  // reachable. (It used a fixed 70px, but the results header is ~250px, so the
  // list ran ~180px past the bottom edge.)
  const panelRef = useRef<HTMLDivElement | null>(null);
  const contentElRef = useRef<HTMLDivElement | null>(null);
  const [chromePx, setChromePx] = useState(70);
  useLayoutEffect(() => {
    const measure = () => {
      const panel = panelRef.current;
      const content = contentElRef.current;
      if (!panel || !content) return;
      const px = Math.round(content.getBoundingClientRect().top - panel.getBoundingClientRect().top);
      if (px > 0) setChromePx((prev) => (Math.abs(prev - px) > 1 ? px : prev));
    };
    measure();
    const panel = panelRef.current;
    if (!panel || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    Array.from(panel.children).forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  });
  const contentMaxHeight = drawerContentMaxHeight(vh, translateY, chromePx);

  return (
    <div className="list-bottom-parent us-drawer">
      <div
        className="floating-panel us-drawer__panel"
        ref={panelRef}
        style={{
          transform: `translateY(${translateY}px)`,
          transition: dragging ? 'none' : 'transform 0.25s ease',
        }}
      >
        <div
          className="handle"
          role="button"
          aria-label="Resize results"
          onTouchStart={(e) => onStart(e.touches[0].clientY)}
          onTouchMove={(e) => onMove(e.touches[0].clientY)}
          onTouchEnd={(e) => onEnd(e.changedTouches[0].clientY)}
          onMouseDown={(e) => onStart(e.clientY)}
        >
          <div className="drag-bar" />
        </div>
        {header}
        <div
          className="panel-content us-drawer__content"
          ref={(el) => {
            contentElRef.current = el;
            contentRef?.(el);
          }}
          onScroll={(e) => onScroll?.(e.currentTarget.scrollTop)}
          style={{
            maxHeight: `${contentMaxHeight}px`,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehaviorY: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default UniversalSearchDrawer;
